import { Button } from "@/components/core/buttons/button";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useInvoicePrintTemplateCreateMutation } from "@/features/dashboard/api/queries/invoice-print-template-query";
import type { Orientation, PageSize, PrintTemplateSettings } from "@/features/dashboard/types/invoice-print-template";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [
	{ value: "a4", label: "A4" },
	{ value: "letter", label: "Letter" },
	{ value: "legal", label: "Legal" },
];

const ORIENTATION_OPTIONS = [
	{ value: "portrait", label: "Portrait" },
	{ value: "landscape", label: "Landscape" },
];

interface CreateInvoicePrintTemplateModalProps {
	isOpen: boolean;
	defaults: PrintTemplateSettings | undefined;
	onClose: () => void;
	onCreated: (id: string) => void;
}

export function CreateInvoicePrintTemplateModal({ isOpen, defaults, onClose, onCreated }: CreateInvoicePrintTemplateModalProps) {
	const [name, setName] = useState("");
	const [pageSize, setPageSize] = useState<PageSize>("a4");
	const [orientation, setOrientation] = useState<Orientation>("portrait");
	const createMutation = useInvoicePrintTemplateCreateMutation();

	const handleClose = () => {
		setName("");
		setPageSize("a4");
		setOrientation("portrait");
		onClose();
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim() || !defaults) return;

		const template = await createMutation.mutateAsync({
			name: name.trim(),
			page_size: pageSize,
			orientation,
			settings: defaults,
		});
		toast.success("Template created");
		handleClose();
		onCreated(template.id);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">New Print Template</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<form className="flex flex-col gap-4" onSubmit={handleSubmit}>
						<div className="flex flex-col gap-1">
							<label htmlFor="template-name" className="text-sm font-medium text-foreground-dark">
								Name
							</label>
							<Input
								id="template-name"
								className="input"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Blank Invoice 2027"
								autoFocus
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="flex flex-col gap-1">
								<span className="text-sm font-medium text-foreground-dark">Page size</span>
								<Select options={PAGE_SIZE_OPTIONS} defaultValue={pageSize} clearable={false} onValueChange={(v) => setPageSize(v as PageSize)} />
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-sm font-medium text-foreground-dark">Orientation</span>
								<Select options={ORIENTATION_OPTIONS} defaultValue={orientation} clearable={false} onValueChange={(v) => setOrientation(v as Orientation)} />
							</div>
						</div>
						<p className="text-sm text-foreground/70">Starts from the default layout — you can reposition every field after creating it.</p>
						<div className="flex justify-end gap-2">
							<Button type="button" variant="ghost" onClick={handleClose} disabled={createMutation.isPending}>
								Cancel
							</Button>
							<Button type="submit" disabled={!name.trim() || createMutation.isPending}>
								{createMutation.isPending ? "Creating..." : "Create Template"}
							</Button>
						</div>
					</form>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
